import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { FaInstagram, FaTiktok, FaFacebookF, FaWhatsapp } from 'react-icons/fa';
import { SocialPost } from '../../types/social';
import { socialService } from '../../services/SocialService';
import { SocialVideoCard } from './SocialVideoCard';
import { SITE_CONFIG } from '../../config/siteConfig';

interface SocialViewProps {
    onClose: () => void;
}

export const SocialView: React.FC<SocialViewProps> = ({ onClose }) => {
    const [posts, setPosts] = useState<SocialPost[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        window.scrollTo(0, 0);
        const loadPosts = async () => {
            try {
                const data = await socialService.getActivePosts();
                setPosts(data);
            } catch (error) {
                console.error("Error loading social posts", error);
            } finally {
                setIsLoading(false);
            }
        };
        loadPosts();
    }, []);

    const socialLinks = [
        { name: 'Instagram', icon: <FaInstagram size={24} />, url: SITE_CONFIG.socialMedia.instagram, color: '#E4405F' },
        { name: 'TikTok', icon: <FaTiktok size={24} />, url: SITE_CONFIG.socialMedia.tiktok, color: '#000000' },
        { name: 'Facebook', icon: <FaFacebookF size={24} />, url: SITE_CONFIG.socialMedia.facebook, color: '#1877F2' },
        { name: 'WhatsApp', icon: <FaWhatsapp size={24} />, url: `https://wa.me/${SITE_CONFIG.contact.whatsapp.replace('+', '')}`, color: '#25D366' },
    ];

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="min-h-screen bg-black"
        >
            {/* Minimalist Sticky Header */}
            <header className="sticky top-0 z-50 bg-black/60 backdrop-blur-xl border-b border-white/5">
                <div className="container mx-auto px-4 h-16 flex items-center justify-between">
                    <button 
                        onClick={onClose}
                        className="flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.2em] text-zinc-500 hover:text-white transition-colors"
                    >
                        <ArrowLeft size={16} />
                        Volver al inicio
                    </button>
                    
                    <div className="flex items-center gap-1">
                        <div className="w-1.5 h-1.5 bg-[#1aaaff] rounded-full animate-pulse" />
                        <span className="text-[10px] font-black uppercase tracking-[0.3em] text-white">Social Feed</span>
                    </div>
                </div>
            </header>

            <main className="container mx-auto px-4 pt-12 pb-24 md:pt-20 md:pb-32">
                {/* Hero Section: Social Networks */}
                <div className="mb-20 text-center">
                    <h1 
                        className="text-4xl md:text-6xl font-display font-black uppercase italic text-white mb-8"
                        style={{ filter: 'drop-shadow(0 0 30px rgba(26,170,255,0.2))' }}
                    >
                        Nuestra <br/> Presencia
                    </h1>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
                        {socialLinks.map((link) => (
                            <a
                                key={link.name}
                                href={link.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="group relative bg-zinc-900/50 border border-zinc-800 p-8 flex flex-col items-center justify-center gap-4 transition-all hover:border-white"
                            >
                                <div className="text-zinc-500 group-hover:text-white transition-colors">
                                    {link.icon}
                                </div>
                                <span className="text-[10px] font-black uppercase tracking-[0.3em] text-zinc-500 group-hover:text-white">
                                    {link.name}
                                </span>
                                
                                {/* Hover Background Effect */}
                                <div className="absolute inset-0 bg-white/5 opacity-0 group-hover:opacity-100 transition-opacity" />
                            </a>
                        ))}
                    </div>
                </div>

                {/* Content Section: Video Feed */}
                <div className="space-y-12">
                    <div className="flex items-center gap-4">
                        <div className="h-px flex-grow bg-zinc-900" />
                        <h2 className="text-xs font-black uppercase tracking-[0.4em] text-zinc-600">Visual Feed</h2>
                        <div className="h-px flex-grow bg-zinc-900" />
                    </div>

                    {isLoading ? (
                        <div className="flex flex-col items-center justify-center py-20 gap-4">
                            <div className="w-8 h-8 border-2 border-[#1aaaff] border-t-transparent rounded-full animate-spin" />
                            <span className="text-[10px] font-black uppercase tracking-widest text-zinc-600">Cargando feed social...</span>
                        </div>
                    ) : posts.length === 0 ? (
                        <div className="text-center py-20">
                            <p className="text-zinc-500 font-mono text-xs uppercase tracking-widest">No hay posts disponibles en este momento.</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6 gap-3 md:gap-4">
                            {posts.map((post) => (
                                <SocialVideoCard key={post.id} post={post} />
                            ))}
                        </div>
                    )}
                </div>
            </main>

        </motion.div>
    );
};
